import type { GameState, PersistenceAdapter, SaveMeta } from './persistence-contract.js'
import { gzip, gunzip } from './utils.js'
import localforage from 'localforage'

export interface SyncQueueEntry {
  uid: string
  saveId: string
  queuedAt: number
  metaTimestamp: number
  compressed: boolean
  stateString: string
}

export interface SyncConflictBackup {
  uid: string
  saveId: string
  kind: 'local_discarded_remote_newer' | 'remote_overwritten_local_newer'
  createdAt: number
  localTimestamp: number
  remoteTimestamp: number
  compressed: boolean
  stateString: string
}

export class SyncManager {
  private PENDING_INDEX_PREFIX = 'pending_saves_index_'
  private BACKUP_INDEX_PREFIX = 'save_backups_index_'
  private SAVE_BACKUP_LIMIT = 30
  private activeSyncStops = new Map<string, () => void>()

  constructor(private cloudAdapter: PersistenceAdapter) {
    localforage.config({
      name: 'AnyQuest',
      storeName: 'vision_cache',
    })
  }

  private pendingIndexKey(uid: string): string {
    return `${this.PENDING_INDEX_PREFIX}${uid}`
  }

  private pendingSaveKey(uid: string, saveId: string): string {
    return `pending_save_${uid}_${saveId}`
  }

  private backupIndexKey(uid: string): string {
    return `${this.BACKUP_INDEX_PREFIX}${uid}`
  }

  private backupItemKey(uid: string, saveId: string, ts: number): string {
    return `save_backup_${uid}_${saveId}_${ts}`
  }

  async enqueue(uid: string, saveId: string, state: GameState): Promise<void> {
    const { history: _h, ...stripped } = state
    const stateString = await gzip(JSON.stringify({ ...stripped, history: [] }))
    
    const entry: SyncQueueEntry = {
      uid,
      saveId,
      queuedAt: Date.now(),
      metaTimestamp: state.meta.timestamp ?? Date.now(),
      compressed: true,
      stateString,
    }
    
    await localforage.setItem(this.pendingSaveKey(uid, saveId), entry)
    
    const idx = await this.getPendingIds(uid)
    if (!idx.includes(saveId)) {
      idx.push(saveId)
      await localforage.setItem(this.pendingIndexKey(uid), idx)
    }
  }

  async getPendingIds(uid: string): Promise<string[]> {
    const raw = await localforage.getItem<string[]>(this.pendingIndexKey(uid))
    return Array.isArray(raw) ? raw : []
  }

  async flush(uid: string): Promise<void> {
    if (typeof navigator !== 'undefined' && !navigator.onLine) return
    
    const saveIds = await this.getPendingIds(uid)
    for (const saveId of [...new Set(saveIds)]) {
      const pending = await localforage.getItem<SyncQueueEntry>(this.pendingSaveKey(uid, saveId))
      if (!pending) {
        await this.removePending(uid, saveId)
        continue
      }

      try {
        const json = pending.compressed ? await gunzip(pending.stateString) : pending.stateString
        const pendingState = JSON.parse(json) as GameState
        const localTs = pending.metaTimestamp || pending.queuedAt

        const remote = await this.cloudAdapter.readRemoteMetaTimestamp?.(uid, saveId) || { exists: false, timestamp: 0 }
        
        if (remote.exists && remote.timestamp > localTs) {
          await this.writeBackup(uid, saveId, 'local_discarded_remote_newer', pendingState, localTs, remote.timestamp)
          await this.removePending(uid, saveId)
          continue
        }

        if (remote.exists && remote.timestamp > 0) {
          const remoteState = await this.cloudAdapter.loadGame(uid, saveId)
          if (remoteState) {
            await this.writeBackup(uid, saveId, 'remote_overwritten_local_newer', remoteState, localTs, remote.timestamp)
          }
        }

        await this.cloudAdapter.saveGame(uid, saveId, pendingState)
        await this.removePending(uid, saveId)
      } catch (e) {
        console.warn('[SyncManager] Flush failed for', saveId, e)
        break
      }
    }
  }

  private async removePending(uid: string, saveId: string): Promise<void> {
    await localforage.removeItem(this.pendingSaveKey(uid, saveId))
    const idx = await this.getPendingIds(uid)
    await localforage.setItem(this.pendingIndexKey(uid), idx.filter(id => id !== saveId))
  }

  private async writeBackup(
    uid: string,
    saveId: string,
    kind: SyncConflictBackup['kind'],
    state: GameState,
    localTimestamp: number,
    remoteTimestamp: number
  ): Promise<void> {
    const createdAt = Date.now()
    const key = this.backupItemKey(uid, saveId, createdAt)
    const { history: _h, ...stripped } = state
    const stateString = await gzip(JSON.stringify({ ...stripped, history: [] }))
    
    const payload: SyncConflictBackup = {
      uid, saveId, kind, createdAt, localTimestamp, remoteTimestamp,
      compressed: true,
      stateString
    }
    
    await localforage.setItem(key, payload)
    
    const idx = await localforage.getItem<string[]>(this.backupIndexKey(uid)) ?? []
    idx.push(key)
    const overflow = idx.length - this.SAVE_BACKUP_LIMIT
    if (overflow > 0) {
      const stale = idx.slice(0, overflow)
      await Promise.allSettled(stale.map(k => localforage.removeItem(k)))
      await localforage.setItem(this.backupIndexKey(uid), idx.slice(overflow))
    } else {
      await localforage.setItem(this.backupIndexKey(uid), idx)
    }
  }

  startAutoSync(uid: string): void {
    if (!uid || this.activeSyncStops.has(uid) || typeof window === 'undefined') return
    
    let flushing = false
    const runFlush = async () => {
      if (flushing) return
      flushing = true
      try { await this.flush(uid) } finally { flushing = false }
    }

    const onOnline = () => { runFlush().catch(console.warn) }
    window.addEventListener('online', onOnline)
    
    const interval = window.setInterval(() => {
      if (navigator.onLine) runFlush().catch(console.warn)
    }, 30000)

    runFlush().catch(console.warn)
    
    this.activeSyncStops.set(uid, () => {
      window.removeEventListener('online', onOnline)
      window.clearInterval(interval)
    })
  }

  stopAutoSync(uid: string): void {
    const stop = this.activeSyncStops.get(uid)
    if (stop) {
      stop()
      this.activeSyncStops.delete(uid)
    }
  }
}
